import { Empty, Toast } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { AnimationDecorators, useContainerBlockContext, useCustomViewBlockContext, ViewBlockProvider } from '@lighthouse/block'
import { CustomViewBlock } from '@lighthouse/block/src/blocks'
import type {
    ActorEvent,
    CustomViewConfig,
    FilterCommonCondition,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewControlHorizontalScrollToAction,
    ViewControlScrollToRecordAction
} from '@lighthouse/core'
import { CUSTOM_VIEW_LAYOUT, DIRECTION } from '@lighthouse/core'
import type { ViewAppendParams } from '@lighthouse/shared'
import {
    CenteredWrapper,
    findBlockById,
    findRecordInPool,
    getCursorStyle,
    getPaddingStyle,
    getWithScopeIdAndRecordIdAndColumnId,
    scroll2EndLeftOrRight,
    scroll2ViewItem,
    scrollToRecord,
    useAppContainerContext,
    useAtomAction,
    useAtomAsyncAction,
    useHandleAbortPrevious,
    useLanguageContext,
    useRegisterViewControlListeners,
    ViewBlockTool
} from '@lighthouse/shared'
import { useBreakpoint } from '@lighthouse/tools'
import { useStore } from 'jotai'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { fetchViewRecordsAtom, loadViewRecordsAtom } from '@/atoms/blockRecordsDict/action'
import { updateRecordAtom } from '@/atoms/dataSource/action'
import { recordPoolAtom } from '@/atoms/dataSource/state'
import { blocksAtom } from '@/atoms/page/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import * as srv from '@/services'

import { useConfigure } from '../hooks/useConfigure'
import { useFetchViewDataDeps } from '../hooks/useFetchViewDataDeps'
import { useFilter } from '../hooks/useFilter'
import { useGroup } from '../hooks/useGroup'
import { useLinkFilter } from '../hooks/useLinkFilter'
import { useQuickFilter } from '../hooks/useQuickFilter'
import { useRenderRichText } from '../hooks/useRenderRichText'
import { useSort } from '../hooks/useSort'
import { useSubscribeView } from '../hooks/useSubscribeView'
import { useTableProps } from '../hooks/useTableProps'
import { useViewIndependent } from '../hooks/useViewIndependent'
import type { CustomControllerProps, FetchViewDataPayload } from '../types'

const EmptyImage = styled.img`
    width: 140px;
    height: 144px;
`

const SCxViewContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const EAGER_LOAD_PAGE_SIZE = 10000

const CustomWaterfallAndGridController: React.FC<CustomControllerProps> = ({ blockData, node, firstRef, onReady, children, ...rest }) => {
    const { scale } = useAppContainerContext()

    const envId = useCurrentEnvId()
    const { id, config, title } = blockData
    const {
        pointer,
        viewType,
        linkFilterController,
        quickFilter,
        groupConfigure,
        canGroup,
        canSort,
        canSearch,
        triggerWorkflow,
        importMode,
        repeat,
        compareFields,
        pagination,
        canPaginate,
        canDragViewSort,
        viewSortConfig,
        breakPoint: baseBreakpoint,
        toolbar,
        recordOperator,
        eventList,
        filter,
        sorts,
        cursor
    } = config as CustomViewConfig
    const { layout: viewLayout, direction: viewDirection = DIRECTION.vertical, wrap = false } = baseBreakpoint.viewLayout ?? {}
    const isCarousel = viewLayout === CUSTOM_VIEW_LAYOUT.carousel
    const isWrappedHorizontalGrid =
        viewLayout === CUSTOM_VIEW_LAYOUT.grid && viewDirection === DIRECTION.horizontal && wrap === true
    const isEagerLoadView = isCarousel || isWrappedHorizontalGrid
    const [extraLoading, setExtraLoading] = useState(false)
    const stackId = useCurrentStackIdContext()
    const { pageId } = useCurrentPageContext()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id)

    const { run: setPageBlocks } = useAtomAction(blocksAtom)
    const { run: updateRecord } = useAtomAsyncAction(updateRecordAtom)
    const { handleActionListTrigger } = useActionTrigger(id)
    const appId = useCurrentAppId()
    const dataSourceList = useDataSourceList(appId, envId)
    const dataSource = useDataSource(appId, envId, pointer)
    // 快捷筛选器hook
    const { quickFiltersRule, handleQuickFilter } = useQuickFilter({
        appId,
        envId,
        blockId: uniqueId,
        dataSource,
        quickFilter
    })

    // 筛选器hook
    const { resolvedFilter } = useFilter({
        filter,
        dataSourceList
    })

    // 联动筛选器hook
    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    // 字段设置hook
    const { tablePropsCache, handleDisplayChange } = useTableProps({
        appId,
        envId,
        blockId: uniqueId
    })

    // 互斥逻辑：启用拖拽排序时禁用sorts配置
    const hasCustomViewSort = baseBreakpoint.viewLayout?.layout === CUSTOM_VIEW_LAYOUT.grid && !!canDragViewSort && !!viewSortConfig?.sortId
    const effectiveSorts = hasCustomViewSort ? undefined : sorts

    // 排序hook
    const { sortCache, mergeSort, handleSorterChange } = useSort({
        appId,
        envId,
        blockId: uniqueId,
        canSort,
        sorts: effectiveSorts
    })

    // 分组hook
    const { initGroup, tabValue, tabList, handleTabChange, getFilterByTab, handleGroupChange, handleGetGroupRecordCount } = useGroup({
        appId,
        envId,
        blockId: uniqueId,
        dataSource,
        canGroup: isCarousel ? false : canGroup,
        groupConfigure: isCarousel ? undefined : groupConfigure
    })

    // 视图独立数据hook
    const {
        searchVal,
        currentPage = 1,
        rowTotal,
        recordPoints,
        handleSearch,
        setViewIndependentData
    } = useViewIndependent({
        blockId: uniqueId,
        pointer,
        canSearch
    })
    const mergedPagination = useMemo(() => ({ ...pagination, currentPage, rowTotal }), [pagination, currentPage, rowTotal])

    const viewIndependentDataOmitRecord = useMemo(
        () => ({
            dsId: dataSource?.id || '',
            currentPage,
            rowTotal,
            search: searchVal
        }),
        [currentPage, dataSource?.id, rowTotal, searchVal]
    )

    const { convertTextByLanguage } = useLanguageContext()

    const allRichtext = useMemo(() => {
        const toolbarRich = toolbar?.map(item => item.name) || []
        const recordOperatorRich = recordOperator?.map(item => item.name) || []

        return [...toolbarRich, ...recordOperatorRich, baseBreakpoint.emptyView?.title].filter(Boolean) as RichTextContentProtocol[]
    }, [toolbar, recordOperator, baseBreakpoint.emptyView?.title])
    const handleRenderRichText = useRenderRichText(allRichtext)

    const { loading, value: fetchViewRecordsValue, run: fetchViewRecords } = useAtomAsyncAction(fetchViewRecordsAtom)

    // 首批数据加载完成且渲染完成后调用 onReady
    const hasCalledReady = useRef(false)
    useEffect(() => {
        if (!loading && fetchViewRecordsValue && !hasCalledReady.current) {
            // 双重 requestAnimationFrame 确保 DOM 渲染完成
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    hasCalledReady.current = true
                    onReady?.()
                })
            })
        }
    }, [loading, onReady, fetchViewRecordsValue])

    const { mutation } = useHandleAbortPrevious(fetchViewRecords)
    const { run: loadMoreRecords } = useAtomAsyncAction(loadViewRecordsAtom)
    const { ref, width: blockWidth, breakPoint } = useBreakpoint([!!fetchViewRecordsValue, extraLoading], { scale })

    const pageSize = isEagerLoadView ? EAGER_LOAD_PAGE_SIZE : pagination.pageSize ?? 10

    // 使用新的依赖管理方案，优化重新渲染（仅包含对象/数组类型字段）
    const stableDeps = useFetchViewDataDeps({
        quickFiltersRule,
        resolvedFilter,
        resolvedLinkFilter,
        sorts: mergeSort
    })

    const fetchViewData = useCallback(
        async (params: FetchViewDataPayload) => {
            const { pageNum } = params
            const isHasTab = !initGroup.groupByFieldId || (initGroup.groupByFieldId && tabValue)
            if (pointer && isHasTab) {
                const quickFiltersWidthTab: FilterCommonCondition[] = [...(stableDeps.quickFiltersRule || []), ...getFilterByTab(tabValue)]
                await mutation({
                    stackId,
                    pageId,
                    viewId: id,
                    uniqueId,
                    scope,
                    isSyncComponent: !!scope,
                    pagination: { currentPage: isEagerLoadView ? 1 : pageNum, pageSize },
                    params: {
                        quickFilters: quickFiltersWidthTab,
                        sorts: stableDeps.sorts,
                        linkFilter: stableDeps.resolvedLinkFilter,
                        filter: stableDeps.resolvedFilter
                    },
                    search: searchVal,
                    disableFetchCount: isEagerLoadView
                })
            }
        },
        [
            initGroup.groupByFieldId,
            tabValue,
            pointer,
            stableDeps.quickFiltersRule,
            stableDeps.sorts,
            stableDeps.resolvedLinkFilter,
            stableDeps.resolvedFilter,
            getFilterByTab,
            mutation,
            stackId,
            pageId,
            id,
            uniqueId,
            scope,
            pageSize,
            isEagerLoadView,
            searchVal
        ]
    )

    const handleLoadMoreRecords = useCallback(
        async (pageNum: number): Promise<RecordLikeProtocol[]> => {
            if (isEagerLoadView || (rowTotal && pageNum > Math.ceil(rowTotal / pageSize))) {
                return []
            }
            const quickFiltersWidthTab: FilterCommonCondition[] = [...(stableDeps.quickFiltersRule || []), ...getFilterByTab(tabValue)]
            const records = await loadMoreRecords({
                stackId,
                pageId,
                scope,
                viewId: id,
                uniqueId,
                isSyncComponent: !!scope,
                pagination: { currentPage: pageNum, pageSize },
                params: {
                    filter: stableDeps.resolvedFilter,
                    quickFilters: quickFiltersWidthTab,
                    linkFilter: stableDeps.resolvedLinkFilter,
                    sorts: stableDeps.sorts
                },
                search: searchVal
            })
            if (!records || records.length === 0) {
                return []
            }
            return records
        },
        [
            rowTotal,
            pageSize,
            isEagerLoadView,
            stableDeps.quickFiltersRule,
            stableDeps.resolvedFilter,
            stableDeps.resolvedLinkFilter,
            stableDeps.sorts,
            getFilterByTab,
            tabValue,
            loadMoreRecords,
            stackId,
            pageId,
            scope,
            id,
            uniqueId,
            searchVal
        ]
    )

    const getGroupRecordCount = useCallback(
        () =>
            handleGetGroupRecordCount({
                quickFilters: stableDeps.quickFiltersRule,
                filter: stableDeps.resolvedFilter,
                linkFilter: stableDeps.resolvedLinkFilter,
                search: searchVal
            }),
        [handleGetGroupRecordCount, stableDeps.quickFiltersRule, stableDeps.resolvedFilter, stableDeps.resolvedLinkFilter, searchVal]
    )

    const handleAction = useCallback(
        (id: string, actorEvent: ActorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger, record]
    )

    const handleRecordClickEvent = useCallback(
        (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) =>
            handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger]
    )

    useConfigure({
        blockData,
        fetchViewData,
        getGroupRecordCount,
        ignorePaginationConfig: isEagerLoadView
    })

    // 接受其他命令执行加载数据操作
    useSubscribeView({ pointer, fetchViewData, pageNum: currentPage })

    // 自定义视图从九宫格水平模式切换为其他模式
    useUpdateEffect(() => {
        ;(async () => {
            if (baseBreakpoint.viewLayout?.direction === DIRECTION.vertical) {
                setViewIndependentData(s => {
                    s.recordPoints = s.recordPoints.slice(0, pageSize)
                })

                setPageBlocks(draft => {
                    const currentBlocks = draft[pageId]
                    if (!currentBlocks) {
                        return
                    }

                    const draftBlock = findBlockById(id, currentBlocks)
                    if (!draftBlock) {
                        return
                    }

                    if (draftBlock.type !== 'view') {
                        return
                    }

                    draftBlock.config.pagination = {
                        ...pagination,
                        currentPage: 1
                    }
                })
                return
            }

            setExtraLoading(true)
            // setViewRecordList(s => [])
            await fetchViewData({
                pageNum: 1
            })
            setExtraLoading(false)
        })()
    }, [baseBreakpoint.viewLayout?.direction])

    const quickFilterData = useMemo(() => {
        return {
            mode: quickFilter?.mode || 'normal',
            rules: quickFiltersRule
        }
    }, [quickFilter?.mode, quickFiltersRule])

    const handleChangePageNum = useCallback(
        (pageNum: number) => {
            fetchViewData({
                pageNum
            })
        },
        [fetchViewData]
    )

    const handleViewRefresh = useCallback(() => {
        fetchViewData({ pageNum: 1 })
    }, [fetchViewData])

    const handleLoadMore = useCallback(() => {
        if (!isEagerLoadView) {
            handleLoadMoreRecords(currentPage + 1)
        }
    }, [currentPage, handleLoadMoreRecords, isEagerLoadView])

    const store = useStore()

    // 扔到上层
    useRegisterViewControlListeners(id, scope, 'view', {
        loadMore: handleLoadMore,
        refresh: handleViewRefresh,
        horizontalScrollTo: (payload: ViewControlHorizontalScrollToAction['params'] | undefined) => {
            if (isWrappedHorizontalGrid) {
                return
            }

            const to = payload?.horizontalScrollTo
            const horizontalScrollDirection = payload?.horizontalScrollDirection
            const viewContainer = ref.current
            const items = viewContainer?.querySelectorAll('[data-view-item]')
            const viewItemsWrapper = items?.[0]?.parentElement
            if (!to || !items || !viewItemsWrapper) {
                return
            }
            const viewContentContainer = viewItemsWrapper?.parentElement
            const currentScrollLeft = viewItemsWrapper?.parentElement?.scrollLeft ?? 0
            const itemOffsetWidth = items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
            const currentIndex = Math.round(currentScrollLeft / itemOffsetWidth)

            let newIndex

            if (!Number.isNaN(Number(to))) {
                newIndex = horizontalScrollDirection === 'left' ? currentIndex - Number(to) : currentIndex + Number(to)
            }

            if (to === 'end') {
                viewContentContainer &&
                    (horizontalScrollDirection === 'left'
                        ? scroll2EndLeftOrRight(viewContentContainer, { left: 0, behavior: 'smooth' })
                        : scroll2EndLeftOrRight(viewContentContainer, { left: viewItemsWrapper.clientWidth, behavior: 'smooth' }))
            }

            if (newIndex === undefined) {
                return
            }
            scroll2ViewItem({ id, scope }, newIndex, { block: 'nearest', inline: 'nearest' })
        },
        scrollToRecord: (payload: ViewControlScrollToRecordAction<string>['params'] | undefined) => {
            const recordNumber = payload?.scrollToRecord
            if (!recordNumber) {
                return
            }

            const allRecords = store.get(recordPoolAtom)
            const viewRecords = recordPoints.reduce<RecordLikeProtocol[]>((acc, item) => {
                const record = findRecordInPool(allRecords, pointer, item.id)
                if (record) {
                    acc.push(record)
                }
                return acc
            }, [])
            const record = viewRecords.find(item => item.content['ID']?.value === recordNumber)
            const recordId = record?.id

            if (recordId) {
                scrollToRecord({ id, scope }, recordId)
            }
        }
    })

    const handleAppend = useCallback(
        async (params: ViewAppendParams) => {
            const isAppend = await srv.appendDataToDataSource({
                ...params,
                sheetDto: {
                    ...params.sheetDto,
                    envId,
                    triggerWorkflow,
                    repeat,
                    compareFields,
                    importMode
                }
            })
            isAppend && Toast.success(convertTextByLanguage('importSuccess'))
            fetchViewData({
                pageNum: currentPage ?? 1
            })
            return isAppend
        },
        [envId, triggerWorkflow, repeat, compareFields, importMode, convertTextByLanguage, fetchViewData, currentPage]
    )

    const handleRecordUpdate = useCallback(
        (recordId: string, content: RecordLikeProtocol['content']) => {
            return updateRecord({ envId, dsId: pointer, id: recordId, content })
        },
        [envId, pointer, updateRecord]
    )

    return useMemo(() => {
        if (!pointer || !dataSource) {
            const emptyImageUrl = getAssetUrl('empty', 'no_datasource.svg')
            return (
                <CenteredWrapper style={{ width: '100%', height: '100%', overflow: baseBreakpoint.size.overflow }}>
                    <Empty
                        style={{ margin: '32px 0' }}
                        icon={<EmptyImage src={emptyImageUrl} alt="请在「右侧栏>配置」中选择数据来源" />}
                        description="请在「右侧栏>配置」中选择数据来源"
                    />
                </CenteredWrapper>
            )
        }

        // 首次加载未完成时（包括请求未开始和请求进行中），不渲染内容，避免闪现"暂无记录"
        if ((firstRef.current && !fetchViewRecordsValue) || extraLoading) {
            return null
        }
        // 首次加载完成，标记为非首次
        if (firstRef.current && fetchViewRecordsValue) {
            firstRef.current = false
        }

        return (
            <AnimationDecorators
                blockId={id}
                blockName={title}
                data-block-type={viewType}
                data-stop-action-propagation
                animation={baseBreakpoint.animation}
                {...rest}
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: baseBreakpoint.size.overflow,
                    ...rest.style,
                    ...(viewType === 'custom'
                        ? { ...getPaddingStyle(baseBreakpoint.viewLayout?.viewPadding, true), ...getCursorStyle(appId, cursor) }
                        : {})
                }}
            >
                <SCxViewContainer ref={ref}>
                    {breakPoint ? (
                        <ViewBlockTool
                            appId={appId}
                            envId={envId}
                            uniqueId={uniqueId}
                            dataSource={dataSource}
                            dataSourceList={dataSourceList}
                            tabList={tabList}
                            tabValue={tabValue}
                            blockData={blockData}
                            quickFilterData={quickFilterData}
                            viewIndependentDataOmitRecord={viewIndependentDataOmitRecord}
                            sortCache={sortCache}
                            groupCache={initGroup}
                            tablePropsCache={tablePropsCache}
                            onTabChange={handleTabChange}
                            onSearch={handleSearch}
                            onFilter={handleQuickFilter}
                            onChangeSorter={handleSorterChange}
                            onChangeDisplay={handleDisplayChange}
                            onChangeGroup={handleGroupChange}
                            onAppend={handleAppend}
                            onToolbarActionTrigger={handleAction}
                            onChangePageNum={handleChangePageNum}
                            onRenderButtonTitle={handleRenderRichText}
                        >
                            <ViewBlockProvider
                                uniqueId={uniqueId}
                                blockData={blockData}
                                dataSource={dataSource}
                                recordPoints={recordPoints}
                                recordPoolAtom={recordPoolAtom}
                                dataSourceList={dataSourceList}
                            >
                                <CustomViewBlock
                                    scale={scale}
                                    node={node}
                                    blockWidth={blockWidth}
                                    breakPoint={breakPoint}
                                    pagination={mergedPagination}
                                    onRenderRichText={handleRenderRichText}
                                    onLoadMoreData={isEagerLoadView ? undefined : handleLoadMoreRecords}
                                    onRecordClick={handleRecordClickEvent}
                                    onUpdateRecord={handleRecordUpdate}
                                />
                            </ViewBlockProvider>
                        </ViewBlockTool>
                    ) : null}
                </SCxViewContainer>
                {children}
            </AnimationDecorators>
        )
    }, [
        pointer,
        dataSource,
        firstRef,
        fetchViewRecordsValue,
        extraLoading,
        id,
        title,
        viewType,
        isEagerLoadView,
        baseBreakpoint.animation,
        baseBreakpoint.size.overflow,
        baseBreakpoint.viewLayout?.viewPadding,
        rest,
        appId,
        cursor,
        ref,
        breakPoint,
        envId,
        uniqueId,
        dataSourceList,
        tabList,
        tabValue,
        blockData,
        quickFilterData,
        viewIndependentDataOmitRecord,
        sortCache,
        initGroup,
        tablePropsCache,
        handleTabChange,
        handleSearch,
        handleQuickFilter,
        handleSorterChange,
        handleDisplayChange,
        handleGroupChange,
        handleAppend,
        handleAction,
        handleChangePageNum,
        handleRenderRichText,
        recordPoints,
        scale,
        node,
        blockWidth,
        mergedPagination,
        handleLoadMoreRecords,
        handleRecordClickEvent,
        handleRecordUpdate,
        children
    ])
}

export default CustomWaterfallAndGridController
